import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IDP_UPLOAD_ACCEPT, supportedFile } from '../../utils/idp/supportedUpload';

function IdpUploadZone({
  disabled,
  file,
  onFile,
  onUnsupportedFile,
  error,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const pick = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    inputRef.current?.click();
  }, []);

  const onChange = useCallback(
    (e) => {
      const f = e.target.files?.[0] ?? null;
      if (f && !supportedFile(f)) {
        onUnsupportedFile?.();
        onFile(null);
        e.target.value = '';
        return;
      }
      onFile(f);
    },
    [onFile, onUnsupportedFile],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      const f = e.dataTransfer.files?.[0] ?? null;
      if (f && !supportedFile(f)) {
        onUnsupportedFile?.();
        onFile(null);
        return;
      }
      onFile(f);
    },
    [disabled, onFile, onUnsupportedFile],
  );

  return (
    <div
      className={`idp-upload-zone idp-upload-zone--square${dragOver ? ' idp-upload-zone--active' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={pick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pick(e);
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t('idpPage.upload.fieldLabel')}
    >
      <div className="idp-upload-zone__head">
        <h3 className="idp-upload-zone__title">{t('idpPage.upload.fieldLabel')}</h3>
        <p className="idp-upload-zone__hint">{t('idpPage.help.upload')}</p>
      </div>
      <div className="idp-upload-zone__body">
        <span className="material-symbols-rounded idp-upload-zone__icon" aria-hidden>
          upload
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={IDP_UPLOAD_ACCEPT}
          hidden
          onChange={onChange}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
        />
        <p className="idp-upload-zone__text">
          {t('idpPage.upload.dropPromptBefore')}
          {' '}
          <button type="button" className="idp-linkish" onClick={pick} disabled={disabled}>
            {t('idpPage.upload.chooseFile')}
          </button>
          {t('idpPage.upload.dropPromptAfter')}
        </p>
        {file ? (
          <small className="idp-upload-zone__filename">
            {t('idpPage.upload.selected')}
            {' '}
            <strong>{file.name}</strong>
          </small>
        ) : (
          <small className="idp-upload-zone__formats">{t('idpPage.upload.supportedFormats')}</small>
        )}
      </div>
      {error ? <p className="idp-error-text idp-upload-zone__error">{error}</p> : null}
    </div>
  );
}

IdpUploadZone.propTypes = {
  disabled: PropTypes.bool,
  file: PropTypes.instanceOf(File),
  onFile: PropTypes.func.isRequired,
  onUnsupportedFile: PropTypes.func,
  error: PropTypes.string,
};

IdpUploadZone.defaultProps = {
  disabled: false,
  file: null,
  onUnsupportedFile: undefined,
  error: null,
};

export default IdpUploadZone;
